import React from "react";
import { Link } from "react-router-dom";
import { gql, useQuery } from "@apollo/client";
import InfiniteScroll from "react-infinite-scroll-component";

import {
  Container,
  List,
  Tooltip,
  Fab,
  FormControl,
  FormControlLabel,
  Switch,
  Stack,
  Typography,
} from "@mui/material";
import {
  SwapVertical as ExportIcon,
  Plus as CreateIcon,
} from "mdi-material-ui";

import Layout from "components/common/Layout";
import SearchInput from "components/common/SearchInput";

import BillingExport from "components/billing/BillingExport";
import DebExport from "components/billing/DebExport";
import UkExport from "components/billing/UkExport";
import InvoicesListItem from "components/billing/InvoicesListItem";

const QUERY = gql`
  query ($first: Int, $after: String, $search: String, $hasPaymentId: Boolean) {
    invoices(
      first: $first
      after: $after
      search: $search
      hasPaymentId: $hasPaymentId
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }

      edges {
        cursor
        node {
          id

          ...InvoicesListItem_invoice
        }
      }
    }
  }

  ${InvoicesListItem.fragments.invoice}
`;

export default function BillingPage() {
  const [search, setSearch] = React.useState("");

  const [pendingPaymentOnly, setPendingPaymentOnly] = React.useState(false);

  const { data, loading, fetchMore } = useQuery(QUERY, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: {
      first: 20,
      search,
      hasPaymentId: pendingPaymentOnly ? false : undefined,
    },
  });

  return (
    <Container fixed>
      <Layout.Toolbar fixed>
        <Stack spacing={2} direction="row" alignItems="center">
          <Tooltip title="Créer une facture">
            <Fab
              size="small"
              color="primary"
              component={Link}
              to="/billing/create-invoice"
              sx={{ flexShrink: 0 }}
            >
              <CreateIcon />
            </Fab>
          </Tooltip>

          <Typography variant="h4" color="primary" sx={{ flexGrow: 1 }}>
            Factures
          </Typography>

          <BillingExport>
            {({ open }) => (
              <Tooltip title="Générer un export comptable CSV">
                <Fab size="small" onClick={open} sx={{ flexShrink: 0 }}>
                  <ExportIcon />
                </Fab>
              </Tooltip>
            )}
          </BillingExport>

          <DebExport>
            {({ open }) => (
              <Tooltip title="Générer un export DEB">
                <Fab size="small" onClick={open} sx={{ flexShrink: 0 }}>
                  <strong>DEB</strong>
                </Fab>
              </Tooltip>
            )}
          </DebExport>

          <UkExport>
            {({ open }) => (
              <Tooltip title="Générer un export UK">
                <Fab size="small" onClick={open} sx={{ flexShrink: 0 }}>
                  <strong>UK</strong>
                </Fab>
              </Tooltip>
            )}
          </UkExport>

          <Stack spacing={1}>
            <SearchInput
              onChange={setSearch}
              FormHelperTextProps={{ component: "div" }}
            />

            <FormControl fullWidth={false}>
              <FormControlLabel
                label="Afficher uniquement les factures non réglées"
                control={
                  <Switch onChange={(evt, val) => setPendingPaymentOnly(val)} />
                }
              />
            </FormControl>
          </Stack>
        </Stack>
      </Layout.Toolbar>

      <List>
        <InfiniteScroll
          scrollableTarget="Layout-Main"
          dataLength={data?.invoices.edges.length ?? 0}
          next={() =>
            fetchMore({
              variables: { after: data?.invoices.pageInfo.endCursor },
            })
          }
          loader={<Layout.Loading />}
          hasMore={data?.invoices.pageInfo.hasNextPage ?? loading}
        >
          {data?.invoices?.edges.map(({ node }) => (
            <InvoicesListItem key={node.id} invoice={node} />
          ))}
        </InfiniteScroll>
      </List>
    </Container>
  );
}
